import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { useFeedOutputs } from "../../api/hooks.js";
import { deleteOutput } from "../../api/outputs.js";
import { nowPlayingStore } from "../../api/now-playing-store.js";
import { Button } from "../../design-system/button.js";
import { cn } from "../../design-system/cn.js";
import { CopyableUrl } from "../../design-system/copyable.js";
import { TonePill } from "../../design-system/feed-state.js";
import { InlineConfirm } from "../../design-system/inline-confirm.js";
import { Panel, StateSurface } from "../../design-system/surfaces.js";
import { useFeedContext } from "./feed-detail-layout.js";
import { OutputDialog } from "./output-dialog.js";
import type { Output } from "../../api/types.js";

/**
 * Everything this feed publishes.
 *
 * Plural from the first row: two deliveries of one feed are two rows and no special
 * case. Each row is the sole authority for what comes out of that address — the
 * engine's configuration and the broker's are rendered from it — which is why this
 * screen shows the row's own values and never a value of its own.
 */
export function FeedOutputsTab() {
  const { feed } = useFeedContext();
  const outputs = useFeedOutputs(feed.id, true);
  const [editing, setEditing] = useState<Output | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const list = outputs.data ?? [];

  function saved() {
    outputs.reload();
    // What a feed publishes is what the public record reports as its deliveries.
    nowPlayingStore.refresh(feed.id);
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">Deliveries</h2>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-ink-3">
            Where this feed is published, and in what format.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          size="small"
        >
          <Plus aria-hidden="true" />
          Add delivery
        </Button>
      </div>

      {outputs.loading && list.length === 0 ? (
        <StateSurface kind="loading" title="Reading this feed's deliveries" />
      ) : outputs.error && list.length === 0 ? (
        <StateSurface
          action={
            <Button onClick={outputs.reload} size="small" variant="secondary">
              Try again
            </Button>
          }
          kind="error"
          title="The deliveries could not be read"
        >
          {outputs.error}
        </StateSurface>
      ) : list.length === 0 ? (
        <StateSurface kind="empty" title="This feed publishes nothing">
          Add a delivery so listeners can tune in.
        </StateSurface>
      ) : (
        <ul className="flex flex-col gap-3">
          {list.map((output) => (
            <li key={output.id}>
              <OutputRow
                feedId={feed.id}
                onChanged={saved}
                onEdit={() => {
                  setEditing(output);
                  setDialogOpen(true);
                }}
                output={output}
              />
            </li>
          ))}
        </ul>
      )}

      <OutputDialog
        feedId={feed.id}
        onClose={() => {
          setDialogOpen(false);
        }}
        onSaved={saved}
        open={dialogOpen}
        output={editing}
      />
    </div>
  );
}

function OutputRow({
  feedId,
  onChanged,
  onEdit,
  output,
}: {
  feedId: string;
  onChanged: () => void;
  onEdit: () => void;
  output: Output;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <Panel className={cn("p-4", !output.enabled && "bg-bg-2")}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="font-mono text-sm text-ink">
              {output.mountPath}
            </span>
            <TonePill
              label={output.enabled ? "Published" : "Not published"}
              tone={output.enabled ? "ok" : "idle"}
            />
            <span
              className="font-mono text-[0.625rem] tracking-[0.08em] text-ink-3 uppercase"
              title={
                output.visibility === "public"
                  ? "Listed by the broker's own directory"
                  : "Reachable at its address, not listed by the broker"
              }
            >
              {output.visibility}
            </span>
          </div>
          <p className="mt-1.5 text-xs text-ink-2">
            {`${output.codec.toUpperCase()} · ${String(output.bitrateKbps)} kbps · ${String(output.sampleRateHz)} Hz · ${output.channels === 1 ? "mono" : "stereo"}`}
          </p>
          <p className="mt-1 font-mono text-[0.6875rem] text-ink-3">
            {`${output.protocol} → ${output.host}:${String(output.port)}`}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            aria-label="Edit delivery"
            onClick={onEdit}
            size="icon-small"
            title="Edit"
            variant="ghost"
          >
            <Pencil aria-hidden="true" />
          </Button>
        </div>
      </div>

      <CopyableUrl
        className="mt-3"
        label="published address"
        value={output.publishedUrl}
      />

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <InlineConfirm
          busy={busy}
          className="min-w-0 flex-1"
          confirmLabel="Withdraw"
          onConfirm={() => {
            setBusy(true);
            setError(null);
            deleteOutput(feedId, output.id)
              .then(onChanged)
              .catch((cause: unknown) => {
                setError(
                  cause instanceof Error
                    ? cause.message
                    : "The delivery was not withdrawn.",
                );
              })
              .finally(() => {
                setBusy(false);
              });
          }}
          question={`Withdraw ${output.mountPath}?`}
          trigger={(open) => (
            <Button
              className="text-ink-3 hover:text-err"
              onClick={open}
              size="small"
              variant="ghost"
            >
              <Trash2 aria-hidden="true" />
              Withdraw
            </Button>
          )}
        >
          Nothing will be published at this address afterwards.
        </InlineConfirm>
        {error ? <span className="text-xs text-err">{error}</span> : null}
      </div>
    </Panel>
  );
}
